import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { useSiloLanguage } from 'component/translation';
import { useSuspenseIsSuspended } from 'data/suspensions';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useEffect } from 'react';
import { LoginBlockedScreen } from 'structure/login';
import { usePersonaKey } from 'util/datastore';

/**
 * Where a suspended user is sent from any app page. Once the user signs out or
 * is no longer suspended, `/suspended` goes back to the page that sent them
 * here. With nothing to go back to, it sends a signed-out user to login and
 * anyone else to their account page, as the login screen does.
 */
export default function SuspendedScreen() {
    const { siloKey } = useLocalSearchParams<'/[siloKey]/suspended'>();
    const profileKey = usePersonaKey();
    const locale = useSiloLanguage();
    const isSuspended = useSuspenseIsSuspended({ siloKey, profileKey, locale });
    const isSignedOut = !profileKey;
    const isUnsuspended = !!profileKey && !isSuspended;
    const shouldGoBack = (isSignedOut || isUnsuspended) && router.canGoBack();

    useEffect(() => {
        if (shouldGoBack) {
            router.back();
        }
    }, [shouldGoBack]);

    if (shouldGoBack) {
        return <LoadingSpinnerContainer />;
    }

    if (isSignedOut) {
        return (
            <Redirect
                href={{
                    pathname: '/[siloKey]/login/one',
                    params: { siloKey },
                }}
            />
        );
    }

    if (isUnsuspended) {
        return (
            <Redirect
                href={{
                    pathname: '/[siloKey]/account/one',
                    params: { siloKey },
                }}
            />
        );
    }

    return <LoginBlockedScreen />;
}
